/**
 * 师资列表页面
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Pagination,
  Card,
  Row,
  Col,
  message,
  Tag,
  Typography,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import facultyService, { Faculty, FacultyQuery, FacultyCategory } from '../services/facultyService';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const FacultyList: React.FC = () => {
  const navigate = useNavigate();
  const [facultyList, setFacultyList] = useState<Faculty[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<FacultyCategory[]>([]);

  // 查询参数
  const [query, setQuery] = useState<FacultyQuery>({
    page: 1,
    size: 20,
    order_by: 'created_at',
    order_direction: 'desc'
  });

  // 筛选参数
  const [filters, setFilters] = useState({
    name: '',
    category: '',
    region: '',
    gender: '',
    certificate_category: ''
  });

  useEffect(() => {
    loadFacultyCategories();
    loadFacultyList();
  }, [query]);

  /**
   * 加载师资类别
   */
  const loadFacultyCategories = async () => {
    try {
      const data = await facultyService.getFacultyCategories();
      setCategories(data);
    } catch (error) {
      message.error('加载师资类别失败');
    }
  };

  /**
   * 加载师资列表
   */
  const loadFacultyList = async () => {
    setLoading(true);
    try {
      const data = await facultyService.getFacultyList(query);
      setFacultyList(data.data);
      setTotal(data.total);
    } catch (error) {
      message.error('加载师资列表失败');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 搜索
   */
  const handleSearch = () => {
    const newQuery = {
      ...query,
      page: 1,
      ...filters
    };
    setQuery(newQuery);
  };

  /**
   * 重置筛选
   */
  const handleReset = () => {
    setFilters({
      name: '',
      category: '',
      region: '',
      gender: '',
      certificate_category: ''
    });
    setQuery({
      page: 1,
      size: 20,
      order_by: 'created_at',
      order_direction: 'desc'
    });
  };

  /**
   * 分页变化
   */
  const handlePageChange = (page: number, size?: number) => {
    setQuery(prev => ({
      ...prev,
      page,
      size: size || prev.size
    }));
  };

  /**
   * 删除师资
   */
  const handleDelete = async (faculty: Faculty) => {
    try {
      await facultyService.deleteFaculty(faculty.id);
      message.success('删除成功');
      loadFacultyList();
    } catch (error) {
      message.error('删除失败');
    }
  };

  /**
   * 表格列定义 - 显示所有17个数据字段
   */
  const columns: ColumnsType<Faculty> = [
    {
      title: '类别',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      fixed: 'left',
      render: (category: string) => (
        <Tag color="blue">{category}</Tag>
      )
    },
    {
      title: '地区',
      dataIndex: 'region',
      key: 'region',
      width: 80
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 100,
      sorter: true
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 60,
      render: (gender: string) => (
        <Tag color={gender === '男' ? 'blue' : 'pink'}>{gender}</Tag>
      )
    },
    {
      title: '简介',
      dataIndex: 'introduction',
      key: 'introduction',
      width: 200,
      render: (introduction: string) => {
        if (!introduction) return '-';
        return introduction.length > 50
          ? `${introduction.substring(0, 50)}...`
          : introduction;
      }
    },
    {
      title: '证书类别',
      dataIndex: 'certificate_category',
      key: 'certificate_category',
      width: 100,
      render: (category: string) => category ? (
        <Tag color="green">{category}</Tag>
      ) : '-'
    },
    {
      title: '证书级别',
      dataIndex: 'certificate_level',
      key: 'certificate_level',
      width: 100,
      render: (level: string) => level || '-'
    },
    {
      title: '证书名称',
      dataIndex: 'certificate_name',
      key: 'certificate_name',
      width: 150,
      render: (name: string) => {
        if (!name) return '-';
        return name.length > 20 ? `${name.substring(0, 20)}...` : name;
      }
    },
    {
      title: '教师资格证',
      dataIndex: 'teacher_certificate',
      key: 'teacher_certificate',
      width: 120,
      render: (cert: string) => cert || '-'
    },
    {
      title: '身份证扫描件',
      dataIndex: 'id_card_file_path',
      key: 'id_card_file_path',
      width: 120,
      render: (path: string) => (
        <Tag color={path ? 'green' : 'default'}>
          {path ? '已上传' : '未上传'}
        </Tag>
      )
    },
    {
      title: '职称证书扫描件',
      dataIndex: 'title_cert_file_path',
      key: 'title_cert_file_path',
      width: 130,
      render: (path: string) => (
        <Tag color={path ? 'green' : 'default'}>
          {path ? '已上传' : '未上传'}
        </Tag>
      )
    },
    {
      title: '相关资格证书扫描件',
      dataIndex: 'qualification_cert_file_path',
      key: 'qualification_cert_file_path',
      width: 150,
      render: (path: string) => (
        <Tag color={path ? 'green' : 'default'}>
          {path ? '已上传' : '未上传'}
        </Tag>
      )
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone: string) => phone || '-'
    },
    {
      title: '课酬(元/天)',
      dataIndex: 'daily_fee',
      key: 'daily_fee',
      width: 110,
      render: (fee: number) => fee ? `¥${fee.toLocaleString()}` : '-',
      sorter: true
    },
    {
      title: '常驻地',
      dataIndex: 'residence',
      key: 'residence',
      width: 100,
      render: (residence: string) => residence || '-'
    },
    {
      title: '当地评价',
      dataIndex: 'local_evaluation',
      key: 'local_evaluation',
      width: 100,
      render: (evaluation: string) => {
        if (!evaluation) return '-';
        let color = 'default';
        if (evaluation.includes('优秀')) color = 'green';
        else if (evaluation.includes('良好')) color = 'blue';
        return <Tag color={color}>{evaluation}</Tag>;
      }
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 150,
      render: (remarks: string) => {
        if (!remarks) return '-';
        return remarks.length > 30 ? `${remarks.substring(0, 30)}...` : remarks;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/faculty/detail/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/faculty/edit/${record.id}`)}
            />
          </Tooltip>
          <Popconfirm
            title="确认删除"
            description={`确定要删除师资"${record.name}"吗？`}
            onConfirm={() => handleDelete(record)}
            okText="删除"
            cancelText="取消"
            okType="danger"
          >
            <Tooltip title="删除">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col flex="auto">
              <Title level={3} style={{ margin: 0 }}>师资管理</Title>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadFacultyList}
                >
                  刷新
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* 筛选条件 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="bottom">
            <Col span={6}>
              <label>姓名:</label>
              <Search
                placeholder="请输入姓名"
                value={filters.name}
                onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
                onSearch={handleSearch}
                style={{ marginTop: 4 }}
              />
            </Col>
            <Col span={4}>
              <label>师资类别:</label>
              <Select
                placeholder="请选择类别"
                value={filters.category}
                onChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
                style={{ width: '100%', marginTop: 4 }}
                allowClear
              >
                {categories.map(cat => (
                  <Option key={cat.id} value={cat.name}>{cat.name}</Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <label>地区:</label>
              <Input
                placeholder="请输入地区"
                value={filters.region}
                onChange={(e) => setFilters(prev => ({ ...prev, region: e.target.value }))}
                style={{ marginTop: 4 }}
              />
            </Col>
            <Col span={3}>
              <label>性别:</label>
              <Select
                placeholder="请选择性别"
                value={filters.gender}
                onChange={(value) => setFilters(prev => ({ ...prev, gender: value }))}
                style={{ width: '100%', marginTop: 4 }}
                allowClear
              >
                <Option value="男">男</Option>
                <Option value="女">女</Option>
              </Select>
            </Col>
            <Col span={4}>
              <label>证书类别:</label>
              <Select
                placeholder="请选择证书类别"
                value={filters.certificate_category}
                onChange={(value) => setFilters(prev => ({ ...prev, certificate_category: value }))}
                style={{ width: '100%', marginTop: 4 }}
                allowClear
              >
                <Option value="创业">创业</Option>
                <Option value="就业">就业</Option>
                <Option value="职业技能">职业技能</Option>
                <Option value="教师证">教师证</Option>
              </Select>
            </Col>
            <Col span={3}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  搜索
                </Button>
                <Button onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={facultyList}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 2400 }}
          onChange={(pagination, filters, sorter) => {
            if (Array.isArray(sorter)) return;

            const { field, order } = sorter;
            if (field && order) {
              setQuery(prev => ({
                ...prev,
                order_by: field as string,
                order_direction: order === 'ascend' ? 'asc' : 'desc'
              }));
            }
          }}
        />

        {/* 分页 */}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={query.page}
            pageSize={query.size}
            total={total}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
            onChange={handlePageChange}
            pageSizeOptions={['10', '20', '50', '100']}
          />
        </div>
      </Card>
    </div>
  );
};

export default FacultyList;